// Shares a link via the native share sheet where available (mobile browsers,
// some desktop browsers); falls back to copying the link to the clipboard.
export async function shareLink(url, title) {
  if (navigator.share) {
    try {
      await navigator.share({ title, url })
      return { shared: true }
    } catch (e) {
      if (e && e.name === 'AbortError') return { shared: false, cancelled: true }
      // fall through to clipboard fallback on any other error
    }
  }
  try {
    await navigator.clipboard.writeText(url)
    return { shared: false, copied: true }
  } catch (e) {
    return { shared: false, copied: false }
  }
}

function downloadBlob(blob, filename) {
  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
}

// Shares a PDF file (so the recipient gets the actual document, not just a
// link to click) alongside the link as accompanying text, via the native
// share sheet's file-sharing support. Most desktop browsers don't support
// attaching files through navigator.share even when they support sharing
// links/text -- when that's the case, this downloads the PDF directly (so
// the user still gets the real file, ready to attach anywhere themselves)
// and copies the link, rather than silently sharing the link alone.
export async function shareLinkWithPdf(url, title, pdfBlob, filename) {
  if (pdfBlob && navigator.canShare) {
    const file = new File([pdfBlob], filename, { type: 'application/pdf' })
    if (navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ title, text: url, files: [file] })
        return { shared: true, withFile: true }
      } catch (e) {
        if (e && e.name === 'AbortError') return { shared: false, cancelled: true }
        // fall through to the download+copy fallback on any other error
      }
    }
  }

  if (pdfBlob) {
    downloadBlob(pdfBlob, filename)
    try {
      await navigator.clipboard.writeText(url)
      return { shared: false, downloaded: true, copied: true }
    } catch (e) {
      return { shared: false, downloaded: true, copied: false }
    }
  }

  return shareLink(url, title)
}
